import React from 'react';
import { Paper, Stack, Title, Chip, TextInput, UnstyledButton, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { SubscriberRegistration } from '../../../services/api';
import { ProgressBar } from '../../../components/Common/ProgressBar';

interface InterestsFormProps {
  form: UseFormReturnType<SubscriberRegistration>;
  isLoadingSuggestions: boolean;
  interestSuggestions: string[];
  additionalChips: string[];
  handleAddChip: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleRemoveChip: (chip: string) => void;
}

export const InterestsForm: React.FC<InterestsFormProps> = ({
  form,
  isLoadingSuggestions,
  interestSuggestions,
  additionalChips,
  handleAddChip,
  handleRemoveChip
}) => {
  const handleAddClick = () => {
    if (form.values.additionalInterests?.trim()) {
      const event = {
        key: 'Enter',
        preventDefault: () => {
          /* prevent default form submission */
        },
        currentTarget: { value: form.values.additionalInterests }
      } as React.KeyboardEvent<HTMLInputElement>;
      handleAddChip(event);
    }
  };

  return (
    <Paper p="xl" radius="md" withBorder>
      <Stack gap="md">
        <Title order={3} size="h4">
          Ihre Interessen
        </Title>
        {isLoadingSuggestions ? (
          <ProgressBar message="Unsere KI sucht gerade spannende Interessensvorschläge basierend auf Ihren Angaben heraus..." />
        ) : (
          <>
            <Text size="s" fw={500}>
              KI-generierte Interessensvorschläge
            </Text>
            <div className="chip-container">
              <Chip.Group
                multiple
                value={form.values.interests}
                onChange={value => form.setFieldValue('interests', value)}
              >
                {interestSuggestions.map((interest, index) => (
                  <Chip
                    key={index}
                    value={interest}
                    className="chip"
                    checked={form.values.interests.includes(interest)}
                  >
                    {interest}
                  </Chip>
                ))}
              </Chip.Group>
            </div>
          </>
        )}

        <Text size="s" fw={500}>
          Weitere Interessen hinzufügen
        </Text>
        {additionalChips.length > 0 && (
          <Text size="xs" c="dimmed" data-path="additionalInterestsRemoveInfo">
            Hinzugefügte Interessen können durch Klick auf den jeweiligen Chip wieder entfernt
            werden
          </Text>
        )}
        <TextInput
          placeholder="z.B. Digitale Transformation"
          value={form.values.additionalInterests}
          onChange={e => form.setFieldValue('additionalInterests', e.currentTarget.value)}
          onKeyDown={handleAddChip}
          data-path="additionalInterests"
          rightSection={
            <UnstyledButton
              onClick={handleAddClick}
              disabled={!form.values.additionalInterests?.trim()}
              style={{
                color: '#228be6',
                fontSize: '14px',
                marginRight: '8px',
                opacity: !form.values.additionalInterests?.trim() ? 0.5 : 1,
                cursor: !form.values.additionalInterests?.trim() ? 'not-allowed' : 'pointer'
              }}
            >
              Hinzufügen
            </UnstyledButton>
          }
          rightSectionWidth={80}
          styles={{
            input: {
              paddingRight: '80px'
            }
          }}
        />
        <div className="chip-container">
          <Chip.Group multiple>
            {additionalChips.map((chip, index) => (
              <Chip
                key={index}
                value={chip}
                className="chip"
                checked={true}
                onClick={() => handleRemoveChip(chip)}
              >
                {chip}
              </Chip>
            ))}
          </Chip.Group>
        </div>
        {form.errors.interests && (
          <Text className="error-message" c="red" size="sm" mt="xs">
            {form.errors.interests}
          </Text>
        )}
      </Stack>
    </Paper>
  );
};
